// src/project/data/services/saveRiderKyc.js
//
// Workflow KYC en 3 étapes (même logique que saveAccountValidationKycData dans le dashboard restaurant) :
//   1. POST /kyc/create/draft  — Crée le brouillon (ou retourne 200 si déjà existant)
//   2. POST /kyc/update/{kycId} — Remplit les données personnelles, identité, véhicule
//   3. POST /kyc/submit/{kycId} — Soumet pour validation (si statut !== 'submitted')

import { API_BASE_URL, getUserId } from '../config/apiConfig'

const GROUP = 'ultraxeno'

function url(path) {
  const sep = path.includes('?') ? '&' : '?'
  return `${API_BASE_URL}${path}${sep}group=${GROUP}`
}

async function post(path, body = {}) {
  const res = await fetch(url(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const json = await res.json()
  return { httpStatus: res.status, json }
}

async function get(path) {
  const res = await fetch(url(path))
  const json = await res.json()
  return { httpStatus: res.status, json }
}

/**
 * Transforme le payload du formulaire KYC rider en corps API V3.
 */
function buildKycBody(userId, kycId, formData) {
  const nameParts = (formData.fullName || '').trim().split(/\s+/)
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''

  const idTypeMap = {
    "Carte nationale d'identité": 'national_id',
    Passeport: 'passport',
    'Permis de conduire': 'driving_license',
    Autre: 'national_id',
  }

  const vehicleTypeMap = {
    Moto: 'motorcycle',
    Voiture: 'car',
    Vélo: 'bicycle',
    Autre: 'other',
  }

  const formatDate = (d) => {
    if (!d) return ''
    const date = new Date(d)
    return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0]
  }

  return {
    id: kycId,
    userId,
    userType: 'rider',
    data: {
      personalInfo: {
        firstName,
        lastName,
        email: formData.email || '',
        dateOfBirth: formatDate(formData.birthDate),
        gender: formData.gender || '',
        nationality: formData.nationality || '',
        phone: formData.phoneNumber?.replace(/[\s\-\(\)]/g, '') || '',
        address: formData.address || '',
        city: formData.city || '',
        country: formData.country || '',
        emergencyContact: formData.emergencyContact || '',
        bankAccount: formData.bankAccount || '',
      },
      identityDocument: {
        type: idTypeMap[formData.idType] || 'national_id',
        number: formData.idNumber || '',
        frontPhotoUrl: formData.idFrontPhotoUrl || '',
        backPhotoUrl: formData.idBackPhotoUrl || '',
        expirationDate: formatDate(formData.idExpiryDate),
      },
      vehicleDocument: {
        type: vehicleTypeMap[formData.vehicleType] || 'other',
        number: formData.vehicleRegistration || '',
        licenseNumber: formData.licenseNumber || '',
        licenseExpiry: formatDate(formData.licenseExpiry),
        photoUrl: '',
      },
      selfieUrl: formData.selfieUrl || '',
    },
  }
}

/**
 * Sauvegarde complète du KYC rider en 3 étapes.
 * @param {Object} formData — Données du formulaire KYC
 * @returns {{ success: boolean, kycId: string|null, status: string, message: string }}
 */
export async function saveRiderKyc(formData) {
  const userId = getUserId()
  if (!userId) {
    return { success: false, message: "Utilisateur non authentifié. Veuillez vous reconnecter.", kycId: null, status: 'error' }
  }

  const kycId = `kyc_rider_${userId}`

  // ──────────────────────────────────────────────
  // ÉTAPE 1 : Créer ou récupérer le brouillon KYC
  // ──────────────────────────────────────────────
  let createRes
  try {
    createRes = await post('/kyc/create/draft', {
      id: kycId,
      userId,
      userType: 'rider',
    })
  } catch (err) {
    console.error('[saveRiderKyc] Étape 1 - Échec réseau:', err)
    return { success: false, message: 'Erreur réseau lors de la création du KYC.', kycId: null, status: 'error' }
  }

  // 200 = déjà existant, 201 = créé. Toute autre réponse est une erreur bloquante.
  const createStatus = createRes.httpStatus
  if (createStatus !== 200 && createStatus !== 201) {
    const msg = createRes.json?.message || 'Échec de la création du brouillon KYC.'
    console.error('[saveRiderKyc] Étape 1 - Erreur API:', createRes)
    return { success: false, message: msg, kycId: null, status: 'error' }
  }

  console.log('[saveRiderKyc] Étape 1 OK — brouillon créé/récupéré:', kycId)

  // ──────────────────────────────────────────────
  // ÉTAPE 2 : Mettre à jour avec les données du formulaire
  // ──────────────────────────────────────────────
  const updateBody = buildKycBody(userId, kycId, formData)
  let updateRes
  try {
    updateRes = await post(`/kyc/update/${kycId}`, updateBody)
  } catch (err) {
    console.error('[saveRiderKyc] Étape 2 - Échec réseau:', err)
    return { success: false, message: 'Erreur réseau lors de la mise à jour du KYC.', kycId, status: 'draft' }
  }

  if (updateRes.httpStatus !== 200 && updateRes.httpStatus !== 201) {
    const msg = updateRes.json?.message || 'Échec de la mise à jour du KYC.'
    console.error('[saveRiderKyc] Étape 2 - Erreur API:', updateRes)
    return { success: false, message: msg, kycId, status: 'draft' }
  }

  console.log('[saveRiderKyc] Étape 2 OK — données KYC enregistrées')

  // Vérifier le statut après update pour décider si on doit soumettre
  let currentKycStatus = updateRes.json?.data?.status || 'draft'

  // ──────────────────────────────────────────────
  // ÉTAPE 3 : Soumettre le KYC (si pas encore soumis)
  // ──────────────────────────────────────────────
  if (currentKycStatus !== 'submitted' && currentKycStatus !== 'approved') {
    try {
      const submitRes = await post(`/kyc/submit/${kycId}`, {})
      if (submitRes.httpStatus === 200 || submitRes.httpStatus === 201) {
        currentKycStatus = 'submitted'
        console.log('[saveRiderKyc] Étape 3 OK — KYC soumis avec succès')
      } else {
        console.warn('[saveRiderKyc] Étape 3 - Soumission échouée (non bloquant):', submitRes.json)
        // Ne pas bloquer : le statut reste "draft", soumis manuellement plus tard
      }
    } catch (err) {
      console.warn('[saveRiderKyc] Étape 3 - Erreur réseau soumission (non bloquant):', err)
    }
  }

  return {
    success: true,
    kycId,
    status: currentKycStatus,
    message: currentKycStatus === 'submitted'
      ? 'KYC soumis avec succès. Il sera validé sous peu.'
      : 'KYC sauvegardé. La soumission sera finalisée prochainement.',
  }
}
